import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/stores/appStore';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Plus, 
  HeartHandshake, 
  Calendar, 
  Target,
  TrendingUp,
  ArrowRight,
  MoreVertical,
  Trash2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { format } from 'date-fns';
import { currencySymbols } from '@/types';
import type { DonationEvent } from '@/types';

export default function Donations() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentOrganization, setDonationEvents, donationEvents, addDonationEvent } = useAppStore();
  
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const currencySymbol = currentOrganization?.currency 
    ? currencySymbols[currentOrganization.currency as keyof typeof currencySymbols] || '$'
    : '$';

  useEffect(() => {
    fetchDonationEvents();
  }, [currentOrganization]);

  const fetchDonationEvents = async () => {
    if (!currentOrganization) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('donation_events')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const events = data || [];

      // Fetch all donations for all events in one query, then aggregate in JS
      const eventIds = events.map((e) => e.id);
      const totalsMap = new Map<string, { total_raised: number; donor_count: number }>();
      if (eventIds.length > 0) {
        const { data: allDonations } = await supabase
          .from('donations')
          .select('event_id, amount')
          .in('event_id', eventIds);

        for (const d of allDonations || []) {
          const existing = totalsMap.get(d.event_id) || { total_raised: 0, donor_count: 0 };
          totalsMap.set(d.event_id, {
            total_raised: existing.total_raised + (d.amount || 0),
            donor_count: existing.donor_count + 1,
          });
        }
      }

      const eventsWithTotals = events.map((event) => {
        const totals = totalsMap.get(event.id) || { total_raised: 0, donor_count: 0 };
        return { ...event, ...totals };
      });

      setDonationEvents(eventsWithTotals);
    } catch (error) {
      console.error('Error fetching donation events:', error);
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Event name is required');
      return;
    }

    if (!currentOrganization) return;

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase
        .from('donation_events')
        .insert({
          organization_id: currentOrganization.id,
          name: name.trim(),
          description: description || null,
          target_amount: targetAmount ? parseFloat(targetAmount) : null,
          start_date: startDate || null,
          end_date: endDate || null,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;

      addDonationEvent({ ...data, total_raised: 0, donor_count: 0 });
      toast.success('Donation event created successfully');
      setIsAddDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error creating donation event:', error);
      toast.error(error instanceof Error ? error.message : t('errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteEvent = async (event: DonationEvent) => {
    if (!confirm('Are you sure you want to delete this donation event?')) return;

    try {
      const { error } = await supabase
        .from('donation_events')
        .delete()
        .eq('id', event.id);

      if (error) throw error;

      fetchDonationEvents();
      toast.success('Donation event deleted successfully');
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error(error instanceof Error ? error.message : t('errors.generic'));
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setTargetAmount('');
    setStartDate('');
    setEndDate('');
  };

  const getProgress = (raised: number, target?: number) => {
    if (!target || target === 0) return 0;
    return Math.min(100, Math.round((raised / target) * 100));
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('navigation.donations')}</h1>
          <p className="text-muted-foreground">
            {donationEvents.length} {donationEvents.length === 1 ? 'event' : 'events'}
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {t('donations.createEvent')}
        </Button>
      </div>

      {/* Events Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      ) : donationEvents.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {donationEvents.map((event) => {
            const progress = getProgress(event.total_raised || 0, event.target_amount);
            const isActive = event.is_active && (!event.end_date || new Date(event.end_date) >= new Date());
            
            return (
              <Card key={event.id} className={`hover:shadow-md transition-shadow ${!isActive ? 'opacity-60' : ''}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => navigate(`/donations/${event.id}`)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg">{event.name}</h3>
                        <Badge variant={isActive ? 'default' : 'secondary'}>
                          {isActive ? 'Active' : 'Ended'}
                        </Badge>
                      </div>
                      {event.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {event.description}
                        </p>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleDeleteEvent(event)}>
                          <Trash2 className="w-4 h-4 mr-2 text-red-500" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Progress */}
                  {event.target_amount && (
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Progress</span>
                        <span className="font-medium">{progress}%</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>
                  )}

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-green-500" />
                      <div>
                        <p className="text-sm font-medium">
                          {currencySymbol}{(event.total_raised || 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-muted-foreground">Raised</p>
                      </div>
                    </div>
                    {event.target_amount && (
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-primary" />
                        <div>
                          <p className="text-sm font-medium">
                            {currencySymbol}{event.target_amount.toLocaleString()}
                          </p>
                          <p className="text-xs text-muted-foreground">Target</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Dates */}
                  {(event.start_date || event.end_date) && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {event.start_date && format(new Date(event.start_date), 'MMM dd')}
                        {event.start_date && event.end_date && ' - '}
                        {event.end_date && format(new Date(event.end_date), 'MMM dd, yyyy')}
                      </span>
                    </div>
                  )}

                  {/* Action */}
                  {isActive ? (
                    <Button 
                      className="w-full"
                      onClick={() => navigate(`/donations/${event.id}`)}
                    >
                      Collect Donation
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button 
                      className="w-full"
                      variant="outline"
                      onClick={() => navigate(`/donations/${event.id}`)}
                    >
                      View History
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <HeartHandshake className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">{t('donations.noEvents')}</h3>
          <p className="text-muted-foreground mb-4">{t('donations.createFirst')}</p>
          <Button onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('donations.createEvent')}
          </Button>
        </div>
      )}

      {/* Create Event Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('donations.createEvent')}</DialogTitle>
            <DialogDescription>
              Create a new donation event
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateEvent} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Event Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Building Fund"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Input
                id="description"
                placeholder="Brief description of the event"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="targetAmount">Target Amount (Optional)</Label>
              <Input
                id="targetAmount"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={targetAmount}
                onChange={(e) => setTargetAmount(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date (Optional)</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">End Date (Optional)</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setIsAddDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Create'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
